/**
 * feat-298 — scroll-spy for the TOC (spec §3, design NOTES §3).
 *
 * Rule: active = the LAST rendered nav target, in document order, whose top is
 * at or above the activation line (`chromeTop + 16px`). Nothing above the first
 * Track ⇒ nothing active.
 *
 * Two TRIGGERS feed the same geometric re-pick (`recompute`, which reads
 * `getBoundingClientRect()` over the rendered targets — the trigger alone never
 * says which section you are "inside"):
 *  - a passive, rAF-throttled window `scroll` listener. This is the one that
 *    keeps the highlight right: an instant scroll (scrollbar drag, Home/End,
 *    Ctrl+F, the language-switch anchor's `scrollTo`, a fast fling) can carry
 *    every banner across the activation line between two frames, and then
 *    nothing ever intersects a 1 px band (QA M1).
 *  - an `IntersectionObserver` on a 1 px band at the activation line. Kept for
 *    the cases with no scroll event: the initial pick on mount, and banners
 *    moving because a section above collapsed/expanded or the viewport resized.
 * Collapsed descendants are unmounted, so they drop out of the set by themselves.
 *
 * During a TOC jump the target is made active immediately and spy updates are
 * ignored until `scrollend` (600 ms fallback) — otherwise the highlight strobes
 * through every Drill the smooth scroll passes.
 */
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { NAV_TARGET_ATTR } from './reviewKeys';

const JUMP_SETTLE_MS = 600;

export interface ScrollSpy {
  activeKey: string | null;
  /** Mark `key` active now and hold the spy still until the scroll settles. */
  beginJump: (key: string) => void;
}

export function useScrollSpy(
  rootRef: RefObject<HTMLElement | null>,
  /** Pixels from the viewport top to the activation line. */
  line: number,
  enabled: boolean,
  /** Anything whose change means the rendered set of targets changed. */
  version: unknown,
): ScrollSpy {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const suppressedRef = useRef(false);
  const lineRef = useRef(line);
  lineRef.current = line;
  const [viewportH, setViewportH] = useState(() =>
    typeof window === 'undefined' ? 0 : window.innerHeight,
  );

  const recompute = useCallback(() => {
    if (suppressedRef.current) return;
    const root = rootRef.current;
    if (!root) return;
    const nodes = root.querySelectorAll<HTMLElement>(`[${NAV_TARGET_ATTR}]`);
    let found: string | null = null;
    for (let i = 0; i < nodes.length; i += 1) {
      if (nodes[i].getBoundingClientRect().top <= lineRef.current) {
        found = nodes[i].getAttribute(NAV_TARGET_ATTR);
      } else {
        break; // document order: everything after is lower still
      }
    }
    setActiveKey(found);
  }, [rootRef]);

  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === 'undefined') return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    const bottom = Math.max(0, viewportH - line - 1);
    const io = new IntersectionObserver(() => recompute(), {
      rootMargin: `-${Math.round(line)}px 0px -${Math.round(bottom)}px 0px`,
    });
    root.querySelectorAll<HTMLElement>(`[${NAV_TARGET_ATTR}]`).forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [enabled, rootRef, line, viewportH, recompute, version]);

  useEffect(() => {
    if (!enabled) return undefined;
    let frame: number | null = null;
    const onScroll = () => {
      if (frame != null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        recompute();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame != null) window.cancelAnimationFrame(frame);
    };
  }, [enabled, recompute]);

  const settleTimer = useRef<number | null>(null);
  const releaseRef = useRef<(() => void) | null>(null);

  const clearPending = useCallback(() => {
    if (releaseRef.current) window.removeEventListener('scrollend', releaseRef.current);
    releaseRef.current = null;
    if (settleTimer.current != null) window.clearTimeout(settleTimer.current);
    settleTimer.current = null;
  }, []);

  const beginJump = useCallback(
    (key: string) => {
      clearPending();
      setActiveKey(key);
      suppressedRef.current = true;
      const release = () => {
        clearPending();
        suppressedRef.current = false;
        // Re-read where the scroll actually landed — but only where the spy runs
        // at all; without an observer there is nothing to keep in sync.
        if (enabled && typeof IntersectionObserver !== 'undefined') recompute();
      };
      releaseRef.current = release;
      window.addEventListener('scrollend', release);
      settleTimer.current = window.setTimeout(release, JUMP_SETTLE_MS);
    },
    [clearPending, enabled, recompute],
  );

  useEffect(() => clearPending, [clearPending]);

  return { activeKey, beginJump };
}
